import type { PracticeDetailFieldKey } from '../../../../../services/practiceDetail';
import { DEFAULT_DM_LOCALE, type DmDoctorForm, type DmPracticeForm } from './dmForms.ts';
import { toE164 } from './dmPhone.ts';

const DEFAULT_COUNTRY_CODE = 'US';
const TWO_LETTER_CODE = /^[A-Za-z]{2}$/;
const COUNTRY_CODES_BY_NAME: Record<string, string> = {
    'united states': 'US',
    'united states of america': 'US',
    usa: 'US',
    canada: 'CA'
};

export type DmPrefillSource = Partial<Record<PracticeDetailFieldKey, string>>;

export interface DmPrefill {
    practice: DmPracticeForm;
    doctor: DmDoctorForm;
    doctorSearch: string;
}

export function toDmCountryCode(country: string): string {
    const trimmed = country.trim();
    if (TWO_LETTER_CODE.test(trimmed)) return trimmed.toUpperCase();
    return COUNTRY_CODES_BY_NAME[trimmed.toLowerCase()] ?? DEFAULT_COUNTRY_CODE;
}

function toRegionCode(state: string): string {
    const trimmed = state.trim();
    return TWO_LETTER_CODE.test(trimmed) ? trimmed.toUpperCase() : '';
}

function joinAddress(street: string, details: string): string {
    return [street.trim(), details.trim()].filter(Boolean).join(', ');
}

export function buildDmPrefill(values: DmPrefillSource): DmPrefill {
    const field = (key: PracticeDetailFieldKey) => (values[key] ?? '').trim();
    const countryCode = toDmCountryCode(field('country'));
    const phone = toE164(field('phone'), countryCode);
    const shipping = {
        name: field('name'),
        email: field('email'),
        address: joinAddress(field('address'), field('addressDetails')),
        zipCode: field('zip'),
        city: field('city'),
        regionCode: toRegionCode(field('state'))
    };
    const contact = { firstname: field('drFirstName'), lastname: field('drLastName'), phone };

    return {
        practice: {
            ...contact,
            locale: DEFAULT_DM_LOCALE,
            countryCode,
            shipping,
            billingSameAsShipping: true,
            billing: shipping
        },
        doctor: { ...contact, email: field('drEmail'), locale: DEFAULT_DM_LOCALE },
        doctorSearch: `${contact.firstname} ${contact.lastname}`.trim()
    };
}
